import { takeLatest, select, put, call, all } from 'redux-saga/effects'
import {
  changeQueueSort,
  setQueueBinsState,
  setQueueSortInput,
  setOriginalMyTasksOrder,
  fetchQueueTxnOppsData
} from 'store/actions';
import {
  queueBinsStateSelector,
  queueSortInputSelector,
  originalMyTasksOrderSelector,
} from 'store/selectors';
import orderBy from 'lodash/orderBy';
import { Constants } from 'utilities/constants';

// Return myTasks bin back to original order (original order is order without any sort applied.)
const returnToOriginalMyTasksOrder = (queueBinsState, originalMyTasksOrder) => {
  const myTasksOrder = originalMyTasksOrder.map(task => {
    return queueBinsState.allTasks.tasks.find(el => el.id === task);
  });
  return {
    ...queueBinsState,
    allTasks: { ...queueBinsState.allTasks, tasks: myTasksOrder }
  };
}

// This is the actual sorting function using lodash's orderBy to sort by a specific order of elements.
// May need to configure filteredSortInput to be able to change the sorting weight
// eslint-disable-next-line require-yield
function* sortMyTasksBin(queueBinsState, changedSortInput) {
  const newMyTasks = [...queueBinsState.allTasks.tasks];
  // Only find the sorts that have an order (asc/desc)
  const filteredSortInput = changedSortInput.filter(el => el.order !== null);
  // Lodash orderBy can sort arrays by multiple fields with different orders (beds asc, zip desc)
  const newMyTaskOrdered = orderBy(newMyTasks, [...filteredSortInput.map(sort => `${sort.label}`)], [...filteredSortInput.map(sort => sort.order)]);
  return newMyTaskOrdered;
}

// This is the function to change the sorting order
// prevQueueSortInput is the state before an order is changed
// sortInput is which label/order object has changed,
// quickChange is used to change from asc/desc or clear sort
// eslint-disable-next-line require-yield
function* changeQueueSortOrder(prevQueueSortInput, sortInput, quickChange) {
  let changedSortInput;
  // On clear (X) click, clear all of the sort orders
  if (quickChange === 'clear') {
    changedSortInput = prevQueueSortInput.map(sortElement => {
      return { ...sortElement, order: null }
    });
  } else {
    changedSortInput = prevQueueSortInput.map(sortElement => {
      if (sortInput && sortInput.toLowerCase().includes(sortElement.label.toLowerCase())) {
        if (quickChange) {    // Quick sort just changes from ascend to descend on chip click
          if (sortElement.order === 'asc') {
            return { ...sortElement, order: 'desc' }
          } else if (sortElement.order === 'desc') {
            return { ...sortElement, order: 'asc' }
          }
        } else {
          // On menu click, switch from asc -> desc -> none
          if (sortElement.order === null) {
            return { ...sortElement, order: 'asc' }
          } else if (sortElement.order === 'asc') {
            return { ...sortElement, order: 'desc' }
          } else if (sortElement.order === 'desc') {
            return { ...sortElement, order: null }
          }
        }
      }
      return sortElement;
    });
  }
  return changedSortInput;
}
// Set the originalMyTasksOrder if there is not one already. If there is no sort applied, set the current myTasks state to originalMyTasks state
// eslint-disable-next-line require-yield
function* getOriginalMyTasksOrder(queueBinsState, prevQueueSortInput, originalMyTasksOrder) {
  let newOriginalMyTasksOrder = originalMyTasksOrder;
  if (!prevQueueSortInput.some(input => input.order !== null) || originalMyTasksOrder.length === 0) {
    newOriginalMyTasksOrder = queueBinsState.allTasks.tasks.map(task => task.id);
  }
  return newOriginalMyTasksOrder;
}
// The main reorder function to set the originalMyTasksOrder and set the order of the myTasks bin
function* reorderTasks(module, queueBinsState, changedSortInput, originalMyTasksOrder) {
  if (changedSortInput.some(input => input.order !== null)) {
    // If there is a sort applied, sort the myTasks bin
    const newSortedMyTasksBin = yield call(sortMyTasksBin, queueBinsState, changedSortInput);
    yield put(setQueueBinsState(module, {
      ...queueBinsState,
      allTasks: {
        ...queueBinsState.allTasks,
        tasks: newSortedMyTasksBin
      }
    }))
  } else {
    // No sort applied, set the myTasks bin back to originalMyTasksOrder
    const reorderedQueueBinsState = returnToOriginalMyTasksOrder(queueBinsState, originalMyTasksOrder);
    yield put(setQueueBinsState(module, reorderedQueueBinsState));
  }
}
export function* changeQueueSortSaga() {
  yield takeLatest(changeQueueSort, function* ({ payload }) {
    const { module, queueSort } = payload;
    const { sortInput, quickChange } = queueSort;
    const [queueBinsState, queueSortInput, originalMyTasksOrder] = yield all([
      select(queueBinsStateSelector, module),
      select(queueSortInputSelector, module),
      select(originalMyTasksOrderSelector, module)
    ]);
    if (queueBinsState !== null) {
      const prevQueueSortInput = [...queueSortInput];
      const newOriginalMyTasksOrder = yield call(getOriginalMyTasksOrder, queueBinsState, prevQueueSortInput, originalMyTasksOrder);
      const changedSortInput = yield call(changeQueueSortOrder, prevQueueSortInput, sortInput, quickChange);
      yield all([
        put(setOriginalMyTasksOrder(module, newOriginalMyTasksOrder)),
        put(setQueueSortInput(module, [...changedSortInput]))
      ]);
      yield call(reorderTasks, module, queueBinsState, changedSortInput, newOriginalMyTasksOrder);
    } else {
      console.log('queueBinsState is null in changeQueueSortSaga');
    }
  });
}
// On myTasks bin change, set the originalMyTasksOrder accordingly.
export function* updateOriginalMyTasksOrderOnQueueChangeSaga() {
  yield takeLatest(setQueueBinsState, function* ({ payload }) {
    const { module, queueBinsState } = payload;
    const [queueSortInput, originalMyTasksOrder] = yield all([
      select(queueSortInputSelector, module),
      select(originalMyTasksOrderSelector, module)
    ]);
    if (queueBinsState !== null) {
      if (queueSortInput.some(el => el.order !== null)) {
        if (queueBinsState.allTasks.tasks.length !== originalMyTasksOrder.length) {
          // On queueBinState update, if items were added/removed, set the originalMyTasksOrder to queueBinsState
          // If a task was ADDED to queueBinsState, remove the sorting. Don't need to remove sorting if a task was removed.
          if (queueBinsState.allTasks.tasks.length > originalMyTasksOrder.length) {
            const changedSortInput = yield call(changeQueueSortOrder, queueSortInput, null, 'clear');
            yield put(setQueueSortInput(module, [...changedSortInput]));
            yield put(fetchQueueTxnOppsData(module));
          }
        }
      } 
    }
  });
}

